import { Platform, Pressable, View } from 'react-native'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'

import Typography from '@ui/typography'
import Icon from '@ui/elements/Icon'
import Border from '@ui/effects/Border'
import Gradient from '@ui/effects/Gradient'

import { TextPaperProps as Props, TextPaperStatus } from './types'
import { TextPaperContext, lineVariant } from './context'
import {
  borderRadius,
  borderWidth,
  colorBackground,
  colorBorder,
  colorLines,
  colorText,
  colorPressed,
} from './styles'

export default function TextPaper({
  label,
  icon,
  iconComponent,
  iconColor,
  lines,
  children,
  onPress,
  status: statusProp,
}: Props) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const status: TextPaperStatus = statusProp || 'default'

  const stylePressed = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [0, 1]),
    }
  })

  const isLinesEmpty = !lines?.length || lines.every((line) => !line?.trim())

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      className="relative inset-0"
      style={{ borderRadius }}
    >
      <Gradient
        colors={colorBackground[status]}
        start={[0.5, 0]}
        end={[0.5, 1]}
        style={{ borderRadius }}
      />
      <Animated.View
        className="pointer-events-none absolute left-0 top-0 h-full w-full"
        style={[
          { backgroundColor: colorPressed[status], borderRadius },
          stylePressed,
        ]}
      />
      <View className="flex flex-col gap-6 px-8 py-6">
        <View className="flex w-full flex-row items-start justify-between">
          <Typography variant="MDB" color={colorText[status]}>
            {label}
          </Typography>
          {icon && (
            <Icon
              name={icon}
              size={24}
              color={iconColor || colorText[status]}
            />
          )}
          {iconComponent}
        </View>
        {!!children && (
          <TextPaperContext.Provider
            value={{ lineColor: colorLines[status], lineVariant }}
          >
            <View className="pb-4">{children}</View>
          </TextPaperContext.Provider>
        )}
        {!isLinesEmpty && lines?.length && (
          <View className="flex flex-col gap-2 pb-4">
            {lines
              .filter((line) => !!line?.trim())
              .map((line, index) => (
                <Typography
                  key={index}
                  variant={lineVariant}
                  color={colorLines[status]}
                  // className="font-text-semibold"
                  style={{
                    minHeight: Platform.select({ default: 21, web: 24 }),
                  }}
                >
                  {line || ''}
                </Typography>
              ))}
          </View>
        )}
      </View>
      <Border
        style={{
          borderRadius,
          borderColor: colorBorder[status],
          borderWidth: borderWidth[status],
        }}
      />
    </Pressable>
  )
}
